import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Database, KeyRound, MessageSquareWarning, Cpu, Users, FileCheck2, Sparkles, ArrowRight, RotateCcw } from "lucide-react";
import { track } from "../../lib/analytics";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const QUESTIONS = [
  { id: "system", label: "What type of AI system is being introduced?", options: ["Employee generative AI / copilot", "Internal knowledge assistant", "Customer-facing assistant", "Autonomous AI agent", "Third-party / supplier AI"] },
  { id: "inputs", label: "What information can users provide to it?", options: ["General, non-sensitive only", "Some sensitive or personal data", "Highly sensitive / restricted material"] },
  { id: "internal_access", label: "Can it access internal systems?", options: ["No", "Read-only access", "Read and write access"] },
  { id: "actions", label: "Can it take actions?", options: ["No — responses only", "Suggests actions for a human", "Executes actions autonomously"] },
  { id: "external", label: "Does it interact externally?", options: ["No", "Sends communications externally", "Connects to external systems"] },
  { id: "human", label: "What decisions require human approval?", options: ["None defined yet", "High-impact actions only", "A defined set by risk level"] },
  { id: "hosting", label: "Where is the underlying model hosted?", options: ["Vendor cloud (commercial)", "Private cloud", "Customer-controlled environment"] },
  { id: "evidence", label: "What evidence is currently retained?", options: ["None / ad-hoc", "Basic logs", "Structured, reviewable records"] },
  { id: "roles", label: "Are different policies required by role?", options: ["No", "Some role differences", "Yes — significant differences"] },
  { id: "violation", label: "What happens when the AI violates policy?", options: ["Nothing enforced today", "Logged only", "Blocked / escalated"] },
];

const CATS = [
  { id: "data", name: "Data", icon: Database },
  { id: "access", name: "Access", icon: KeyRound },
  { id: "output", name: "Output", icon: MessageSquareWarning },
  { id: "action", name: "Action", icon: Cpu },
  { id: "oversight", name: "Human oversight", icon: Users },
  { id: "audit", name: "Audit", icon: FileCheck2 },
];

function buildArchitecture(a) {
  const rec = { data: [], access: [], output: [], action: [], oversight: [], audit: [] };
  // Data
  rec.data.push("Inspect inbound prompts and uploads against your data policy");
  if (a.inputs && a.inputs !== "General, non-sensitive only") rec.data.push("Redact or block designated sensitive content before it reaches the model");
  if (a.inputs === "Highly sensitive / restricted material") rec.data.push("Minimise stored content; retain a reference rather than the raw data");
  // Access
  if (a.roles && a.roles !== "No") rec.access.push("Role-based policies for what each role may send and receive");
  rec.access.push("Constrain which sources and tools the system may reach");
  if (a.internal_access === "Read and write access") rec.access.push("Scope internal access and require approval for write operations");
  // Output
  rec.output.push("Inspect designated outputs before they reach users or workflows");
  if (a.system === "Customer-facing assistant") rec.output.push("Intercept unsupported or out-of-scope responses before release");
  // Action
  if (a.actions === "Executes actions autonomously" || a.actions === "Suggests actions for a human") {
    rec.action.push("Evaluate proposed actions against an action policy / allow-list");
    rec.action.push("Hold high-impact actions for authorisation before execution");
  } else {
    rec.action.push("No action governance required today — revisit if the system gains tools");
  }
  if (a.external && a.external !== "No") rec.action.push("Govern external communications and connections by policy");
  // Oversight
  rec.oversight.push("Route designated interactions to authorised human reviewers");
  if (a.human === "None defined yet") rec.oversight.push("Define which risk levels require human approval");
  else rec.oversight.push("Apply your defined human-approval thresholds by risk level");
  // Audit
  rec.audit.push("Record event, policy, action, reviewer and outcome for each intervention");
  if (a.evidence !== "Structured, reviewable records") rec.audit.push("Introduce structured, reviewable evidence in place of ad-hoc logs");
  if (a.violation !== "Blocked / escalated") rec.audit.push("Enforce block / escalate responses to policy violations, not logging alone");
  return rec;
}

export default function AssuranceExplorer() {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const answered = Object.keys(answers).length;
  const complete = answered >= QUESTIONS.length;

  const generate = () => {
    setResult(buildArchitecture(answers));
    track("defence_assurance_generated", { answered });
    setTimeout(() => document.getElementById("assurance-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const reset = () => { setAnswers({}); setResult(null); };

  return (
    <div data-testid="defence-assurance-explorer">
      <div className="grid sm:grid-cols-2 gap-4">
        {QUESTIONS.map((q) => (
          <div key={q.id} className="rounded-xl border border-white/10 bg-navy-900/60 p-4">
            <label className="block text-sm font-medium text-foreground">{q.label}</label>
            <Select value={answers[q.id] || ""} onValueChange={(v) => setAnswers((s) => ({ ...s, [q.id]: v }))}>
              <SelectTrigger className="mt-2.5 bg-navy-800 border-white/12" data-testid={`assurance-${q.id}`}>
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent className="bg-navy-800 border-white/12">
                {q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={generate}
          disabled={answered === 0}
          data-testid="assurance-generate"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          <Sparkles className="h-4 w-4" /> {complete ? "Generate control architecture" : `Generate (${answered}/${QUESTIONS.length} answered)`}
        </button>
        {(result || answered > 0) && (
          <button onClick={reset} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5">
            <RotateCcw className="h-4 w-4" /> Reset
          </button>
        )}
      </div>

      {result && (
        <div id="assurance-result" className="mt-8 scroll-mt-24">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">Illustrative control architecture</p>
          <p className="mt-1 text-sm text-muted-foreground">Where norse3 could sit around this deployment. This is not a security accreditation or regulatory approval.</p>
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {CATS.map((c) => {
              const Icon = c.icon;
              const items = result[c.id];
              return (
                <div key={c.id} className="rounded-lg border border-white/10 bg-navy-900/60 p-5">
                  <div className="flex items-center gap-2.5">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h4 className="text-sm font-bold uppercase tracking-wide text-foreground">{c.name}</h4>
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {items.map((it, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground leading-relaxed">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <div className="mt-6">
            <Link to="/defence#briefing" onClick={() => track("defence_assurance_cta")} className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="assurance-cta">
              Discuss this architecture with norse3 <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
